/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$link-font-size: 18px;
$link-font-size-small: 14px;
$link-line-height: 27px;
$link-line-height-small: 21px;
$link-min-height: 32px;
$link-min-height-small: 24px;
$link-font-weight: $font-weight-bold;
$link-margin-icon-text-autosize: 8px;
$link-margin-icon-text: 4px;
$link-border-bottom-width: 2px !default;
$link-border-bottom-width-small: 1px !default;
$link-hover-decoration: underline !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  position: relative;
  margin: 0px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  --fontWeight: #{$link-font-weight};
  --fontSize: #{$link-font-size};
  --lineHeight: #{$link-line-height};
  --borderBottomWidth: #{$link-border-bottom-width};
  --minHeight: #{$link-min-height};

  .#{$prefix}link-info-icon {
    align-self: flex-start;
    margin-left: 16px;
    @include iconTextAligner($link-min-height, 20px);
  }
}

:host([size="small"]) {
  --fontWeight: #{$link-font-weight};
  --fontSize: #{$link-font-size-small};
  --lineHeight: #{$link-line-height-small};
  --minHeight: #{$link-min-height-small};
  --borderBottomWidth: #{$link-border-bottom-width-small};

  .#{$prefix}link-info-icon {
    @include iconTextAligner($link-min-height-small, 20px);
  }
}

:host(.#{$prefix}inline) {
  .#{$prefix}link {
    display: inline;
    padding: 0;
    text-decoration: underline;

    .#{$prefix}text-wrapper {
      display: inline;
    }

    &:hover {
      color: v(inlineLinkHoverColor);
    }

    &:focus-visible {
      text-decoration: none;
    }
  }
}

button.#{$prefix}link {
  // button styles reset
  box-shadow: none;
  padding: 0px;
  border: none;
  background-color: transparent;
  outline: none;
  text-decoration: none;
  transform: translate3d(0px, 0px, 0px);
}

.#{$prefix}link {
  font-family: $font-family-base;
  font-weight: var(--fontWeight);
  font-size: var(--fontSize);
  line-height: var(--lineHeight);
  display: inline-flex;
  align-items: flex-start;
  text-decoration: none;
  // Due to a bug in Safari we cannot animate text-decoration :(
  // text-decoration: underline;
  // text-decoration-color: transparent;
  // text-decoration-thickness: v(borderBottomWidth);
  color: v(contentColor);
  cursor: pointer;
  max-width: 100%;
  transition-property: color, text-decoration-color;
  transition-duration: $transition-duration;
  transition-timing-function: ease-in-out;

  .#{$prefix}icon-wrapper {
    eon-ui-icon {
      transition: fill $transition-duration ease-in-out;
      fill: v(contentColor);
    }
  }

  .#{$prefix}text-wrapper {
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: calc((32px - var(--lineHeight)) / 2);
    margin-bottom: calc((32px - var(--lineHeight)) / 2);
  }

  span.#{$prefix}text {
    border-bottom: solid v(borderBottomWidth) transparent;
    transition: border $transition-duration ease-in-out;
    overflow-wrap: anywhere;
  }

  &.#{$prefix}size-small {
    span.#{$prefix}text {
      border-bottom: solid v(borderBottomWidthSmall) transparent;
    }
  }

  &.#{$prefix}size-small {
    .#{$prefix}text-wrapper {
      margin-top: calc((24px - var(--lineHeight)) / 2);
      margin-bottom: calc((24px - var(--lineHeight)) / 2);
    }
    span.#{$prefix}text {
      border-bottom: solid v(borderBottomWidthSmall) transparent;
      margin-top: calc((24px - var(--lineHeight)) / 2);
      margin-bottom: calc((24px - var(--lineHeight)) / 2);
    }
  }

  &.#{$prefix}icon-position-left {
    &.#{$prefix}autosize {
      .#{$prefix}icon-wrapper + .#{$prefix}text-wrapper {
        margin-left: $link-margin-icon-text-autosize;
      }
    }

    .#{$prefix}icon-wrapper + .#{$prefix}text-wrapper {
      margin-left: $link-margin-icon-text;
    }
  }

  &.#{$prefix}icon-position-right {
    &.#{$prefix}autosize {
      .#{$prefix}text-wrapper + .#{$prefix}icon-wrapper {
        margin-left: $link-margin-icon-text-autosize;
      }
    }

    .#{$prefix}text-wrapper + .#{$prefix}icon-wrapper {
      margin-left: $link-margin-icon-text;
    }
  }

  &.#{$prefix}hover,
  &:hover {
    text-decoration: $link-hover-decoration !important;
    // Due to a bug in Safari we cannot animate text-decoration :(
    // text-decoration-color: inherit;
    color: v(contentHoverColor);

    .#{$prefix}icon-wrapper {
      .#{$prefix}icon {
        fill: v(contentHoverColor);
      }
    }
  }

  &:focus-visible {
    @include focusBorder(v(focusColor), 2px);
    border-radius: 4px;
    z-index: 1;
    color: v(contentFocusColor);

    .#{$prefix}icon-wrapper {
      .#{$prefix}icon {
        fill: v(contentFocusColor);
      }
    }
  }

  &.#{$prefix}visibility-always,
  &.#{$prefix}visibility-onHover {
    cursor: default;
  }

  &.#{$prefix}visibility-onClick {
    cursor: pointer;
  }

  &.#{$prefix}disabled {
    opacity: 0.5;
    pointer-events: none;
    color: v(contentDisableColor);

    .#{$prefix}icon-wrapper {
      align-self: flex-start;

      eon-ui-icon {
        fill: v(contentDisableColor);
      }
    }

    &:focus-visible {
      outline: none;
      pointer-events: none;
      color: v(contentDisableColor);

      .#{$prefix}icon-wrapper {
        .#{$prefix}icon {
          fill: v(contentDisableColor);
        }
      }
    }

    &.#{$prefix}hover,
    &:hover {
      pointer-events: none;
      color: v(contentDisableColor);
      text-decoration: none;

      .#{$prefix}icon-wrapper {
        .#{$prefix}icon {
          fill: v(contentDisableColor);
        }
      }
    }
  }
}
